import { useEffect, useState } from "react";
import type { ComponentType } from "react";
import { useMetaTags } from "../components/MetaTags";
import { BrandLink } from "../components/BrandLink";

export function Demo() {
  useMetaTags({
    title: "Try OmniCanvas — Free Interactive Infinite Canvas Demo",
    description:
      "Try the OmniCanvas demo in your browser. Draw, sketch, type and explore spatial notetaking with mind map, kanban and SWOT templates. No signup.",
    url: "https://omnicanvasnotes.com/demo",
  });

  const [Canvas, setCanvas] = useState<ComponentType | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // The canvas chunk is fetched over the network, so it can fail: a flaky
    // connection, or a deploy that replaced the hashed asset while this
    // document was open. Production reported 15 "Failed to fetch dynamically
    // imported module: /assets/DemoCanvas-*.js" errors, and because the import
    // had no rejection handler every one of them left the visitor staring at
    // "Loading the canvas…" forever on the page our blog CTAs point at.
    // Retry once (that clears a transient failure), then surface a real
    // fallback instead of an unhandled rejection.
    const load = (attempt: number) => {
      import("./DemoCanvas")
        .then((m) => {
          if (!cancelled) setCanvas(() => m.DemoCanvas);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt === 0) {
            setTimeout(() => {
              if (!cancelled) load(1);
            }, 1200);
            return;
          }
          setLoadFailed(true);
        });
    };
    load(0);

    return () => {
      cancelled = true;
    };
  }, []);

  if (Canvas) {
    return <Canvas />;
  }

  // SSR / pre-canvas shell. This is what search engines and the first paint
  // see, so it carries real, crawlable copy describing the demo (the live
  // Excalidraw canvas replaces it on the client once it finishes loading).
  return (
    // No pt-16: /demo suppresses the fixed marketing Navbar (see App.tsx), so
    // there is nothing above this to offset.
    <div className="min-h-screen bg-white">
      <div className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-3 sm:px-6">
          <BrandLink />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900">
              Interactive OmniCanvas Demo
            </h1>
            <p className="hidden text-sm text-slate-500 sm:block">
              Draw, sketch, and explore spatial notetaking — the real canvas
              engine, right in your browser. No signup required.
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <p
          className={`text-sm mb-8 ${loadFailed ? "text-slate-600" : "text-slate-400"}`}
          style={{ minHeight: "1.5rem" }}
        >
          {loadFailed ? (
            <>
              The canvas didn&apos;t load — that&apos;s usually a dropped
              connection or a fresh deploy.{" "}
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="font-semibold text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
              >
                Reload the page
              </button>{" "}
              to try again.
            </>
          ) : (
            "Loading the canvas…"
          )}
        </p>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          Try spatial notetaking right now — nothing to install
        </h2>
        <p className="text-slate-600 leading-relaxed mb-6">
          This interactive demo runs the same infinite-canvas engine that powers
          OmniCanvas. Pan and zoom across a boundless surface, type notes
          anywhere, sketch freehand, and connect ideas with arrows — all in your
          browser, with no account required. Nothing you create here is saved.
        </p>
        <p className="text-slate-600 leading-relaxed mb-8">
          Start from a blank canvas or load a ready-made template — a{" "}
          <strong>Mind Map</strong> for brainstorming, a <strong>Kanban</strong>{" "}
          board for tracking work, or a <strong>SWOT analysis</strong> for
          planning. When you&apos;re ready to keep your work and sync it across
          devices, create a free account.
        </p>
        <a
          href="https://app.omnicanvasnotes.com/?mode=try&action=new-note&utm_source=marketing&utm_medium=demo"
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-indigo-700 transition-colors"
        >
          Start a canvas — no account
        </a>
      </div>
    </div>
  );
}
